/**
 * Amplitude event definitions for the Opportunities, Scripts, and
 * Pre Flight – Scripts (script review) modules.
 *
 * Naming: Object–Action Title Case (Amplitude recommended).
 * Each event has a typed convenience function that injects common props.
 *
 * Note: `Script Review Completed`/`Failed` are fired from the preflight
 * worker (backend), so they intentionally have NO constant/helper here —
 * that keeps completion tracking off the frontend by construction.
 */
import { trackAnonymousEvent, trackEvent } from '@/lib/analytics';
import { getCommonEventProps } from '@/lib/analytics-utils';

// ─── Event Name Constants ───────────────────────────────────────────

export const AnalyticsEvents = {
  // Opportunities
  OPPORTUNITY_SET_CREATED: 'Opportunity Set Created',
  OPPORTUNITY_REJECTED: 'Opportunity Rejected',
  OPPORTUNITY_SCRIPT_GENERATED: 'Opportunity Script Generated',
  OPPORTUNITY_CSV_DOWNLOADED: 'Opportunity CSV Downloaded',
  OPPORTUNITY_EXAMPLES_DRAWER_OPENED: 'Opportunity Examples Drawer Opened',
  OPPORTUNITY_EXAMPLES_TAB_SELECTED: 'Opportunity Examples Tab Selected',
  // Scripts
  SCRIPT_AI_EDITED: 'Script AI Edited',
  SCRIPT_COPIED: 'Script Copied',
  SCRIPT_CSV_DOWNLOADED: 'Script CSV Downloaded',
  // Pre Flight – Scripts (script review)
  SCRIPT_REVIEW_CREATED: 'Script Review Created',
  SCRIPT_REVIEW_REPORT_VIEWED: 'Script Review Report Viewed',
  SCRIPT_REVIEW_SHARE_LINK_CREATED: 'Script Review Share Link Created',
  SCRIPT_REVIEW_SHARE_LINK_REVOKED: 'Script Review Share Link Revoked',
  SCRIPT_REVIEW_SHARE_LINK_COPIED: 'Script Review Share Link Copied',
  SCRIPT_REVIEW_NOTE_POSTED: 'Script Review Note Posted',
  SCRIPT_REVIEW_COMMENT_DELETED: 'Script Review Comment Deleted',
  SCRIPT_REVIEW_COMMENTS_BULK_DELETED: 'Script Review Comments Bulk Deleted',
  SCRIPT_REVIEW_GUEST_PAGE_VIEWED: 'Script Review Guest Page Viewed',
  SCRIPT_REVIEW_COMMENT_RESOLVED: 'Script Review Comment Resolved',
  SCRIPT_REVIEW_READY_CHANGED: 'Script Review Ready Changed',
  SCRIPT_REVIEW_SUGGESTION_ACTIONED: 'Script Review Prompt Suggestion Actioned',
  SCRIPT_REVIEW_MATCH_RESOLVED: 'Script Review Auto Match Resolved',
} as const;

// ─── Typed Convenience Functions ────────────────────────────────────

export function trackOpportunitySetCreated(props: {
  opportunity_set_id: string;
  opportunity_set_name: string;
  industry: string;
  products?: string;
  ad_platforms?: string[];
  strategic_priorities?: string;
  brand_voice?: string;
  creation_source: string;
}): void {
  trackEvent(AnalyticsEvents.OPPORTUNITY_SET_CREATED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackOpportunityRejected(props: {
  opportunity_id: string;
  opportunity_set_id: string;
  topic_title: string;
  rejection_reason_length: number;
}): void {
  trackEvent(AnalyticsEvents.OPPORTUNITY_REJECTED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackOpportunityScriptGenerated(props: {
  opportunity_ids: string[];
  opportunity_count: number;
  opportunity_set_id: string;
  trigger_source: string;
}): void {
  trackEvent(AnalyticsEvents.OPPORTUNITY_SCRIPT_GENERATED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackOpportunityCsvDownloaded(props: {
  row_count: number;
  opportunity_set_id: string;
  active_filters?: Record<string, unknown>;
  selected_count?: number;
  export_duration_ms: number;
}): void {
  trackEvent(AnalyticsEvents.OPPORTUNITY_CSV_DOWNLOADED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackOpportunityExamplesDrawerOpened(props: {
  opportunity_id: string;
  opportunity_set_id: string;
  topic_title: string;
  tab_context: string;
}): void {
  trackEvent(AnalyticsEvents.OPPORTUNITY_EXAMPLES_DRAWER_OPENED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackOpportunityExamplesTabSelected(props: {
  opportunity_id: string;
  opportunity_set_id: string;
  tab_selected: string;
  tab_previous: string;
  creative_count_in_tab: number;
  time_on_previous_tab_ms: number;
}): void {
  trackEvent(AnalyticsEvents.OPPORTUNITY_EXAMPLES_TAB_SELECTED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptAiEdited(props: {
  script_ids: string[];
  script_count: number;
  edit_mode: 'single' | 'bulk';
  edit_prompt_length: number;
  opportunity_set_id: string;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_AI_EDITED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptCopied(props: {
  script_ids: string[];
  script_count: number;
  copy_mode: 'single' | 'bulk';
  copy_format: string;
  opportunity_set_id: string;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_COPIED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptCsvDownloaded(props: {
  script_ids: string[];
  script_count: number;
  opportunity_set_id: string;
  export_source: string;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_CSV_DOWNLOADED, {
    ...getCommonEventProps(),
    ...props,
  });
}

// ─── Pre Flight – Scripts (script review) ───────────────────────────
// Authenticated app events (hub + detail page) inject common props.

/** What the video was judged against — 'iteration' = a reference video + brief; 'clean' = nothing. */
type ScriptReviewSource = 'set' | 'upload' | 'iteration' | 'clean';

export function trackScriptReviewCreated(props: {
  review_id: string;
  script_source: ScriptReviewSource;
  opportunity_set_id?: string;
  script_id?: string;
  video_size_bytes: number;
  script_file_type?: string;
  /** Iteration mode only. */
  reference_video_size_bytes?: number;
  brief_length?: number;
  /**
   * Set mode: true when the admin ran with the script the AI matched, false when
   * they overrode it. The acceptance rate is how we know whether matching is
   * actually trustworthy — undefined when no match was offered.
   */
  script_auto_matched?: boolean;
  /** Set when this review is a new VERSION of an existing one. */
  previous_review_id?: string;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_CREATED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptReviewReportViewed(props: {
  review_id: string;
  comment_count: number;
  note_count: number;
  script_source: ScriptReviewSource;
  has_share_link: boolean;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_REPORT_VIEWED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptReviewShareLinkCreated(props: { review_id: string }): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_SHARE_LINK_CREATED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptReviewShareLinkRevoked(props: { review_id: string }): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_SHARE_LINK_REVOKED, {
    ...getCommonEventProps(),
    ...props,
  });
}

export function trackScriptReviewShareLinkCopied(props: { review_id: string }): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_SHARE_LINK_COPIED, {
    ...getCommonEventProps(),
    ...props,
  });
}

/** In-app (authenticated) note; the helper hardcodes the author kind. */
export function trackScriptReviewNotePosted(props: {
  review_id: string;
  note_kind: 'reply' | 'standalone';
  body_length: number;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_NOTE_POSTED, {
    ...getCommonEventProps(),
    author_kind: 'app_user',
    ...props,
  });
}

/** Adge-admin moderation. `comment_kind` distinguishes AI comments from notes. */
export function trackScriptReviewCommentDeleted(props: {
  review_id: string;
  comment_kind: 'ai_comment' | 'note';
  comment_id: string;
  /** True when a rejection reason was given (spawns a prompt-improvement ticket). */
  has_reason?: boolean;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_COMMENT_DELETED, {
    ...getCommonEventProps(),
    ...props,
  });
}

/** An Adge admin cleared one review version's timeline (AI comments only, or everything). */
export function trackScriptReviewCommentsBulkDeleted(props: {
  review_id: string;
  scope: 'ai' | 'all';
  ai_comment_count: number;
  note_count: number;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_COMMENTS_BULK_DELETED, {
    ...getCommonEventProps(),
    ...props,
  });
}

/**
 * The creation form's auto-match finished. One event with an outcome beats three
 * near-identical ones — `no_match` vs `error` is the split that matters (the
 * model declining to guess is working as designed; a job blowing up is not).
 */
export function trackScriptReviewMatchResolved(props: {
  opportunity_set_id: string;
  outcome: 'matched' | 'no_match' | 'error';
  confidence?: number;
  script_id?: string;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_MATCH_RESOLVED, {
    ...getCommonEventProps(),
    ...props,
  });
}

/** Prompt-improvement ticket actioned by an Adge admin (approve applies the edit). */
export function trackScriptReviewSuggestionActioned(props: {
  rejection_id: string;
  action: 'approved' | 'ignored';
  prompt_key: string;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_SUGGESTION_ACTIONED, {
    ...getCommonEventProps(),
    ...props,
  });
}

/**
 * Readiness: a thread was marked resolved / unresolved. `resolved: false` rows are
 * the un-resolve signal — useful for spotting AI comments that get toggled back.
 */
export function trackScriptReviewCommentResolved(props: {
  review_id: string;
  comment_kind: 'ai_comment' | 'note';
  comment_id: string;
  resolved: boolean;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_COMMENT_RESOLVED, {
    ...getCommonEventProps(),
    ...props,
  });
}

/** Readiness: the client-review sign-off was set or removed. */
export function trackScriptReviewReadyChanged(props: {
  review_id: string;
  ready: boolean;
  version_number: number;
  ai_resolved_pct: number;
  human_total: number;
  ai_total: number;
}): void {
  trackEvent(AnalyticsEvents.SCRIPT_REVIEW_READY_CHANGED, {
    ...getCommonEventProps(),
    ...props,
  });
}

// ─── Guest (public /sr/:token) events ───────────────────────────────
// The guest page is OUTSIDE _authenticated. If an internal user opens a share
// link while logged in, the main Amplitude session still carries their userId and
// tenant group — guest events therefore use trackAnonymousEvent (a dedicated
// instance, never identify'd) instead of trackEvent. Never include the share
// token itself (a capability secret) — review_id is the join key.

export function trackScriptReviewGuestPageViewed(props: {
  review_id: string;
  comment_count: number;
  note_count: number;
}): void {
  trackAnonymousEvent(AnalyticsEvents.SCRIPT_REVIEW_GUEST_PAGE_VIEWED, { ...props });
}

export function trackScriptReviewGuestNotePosted(props: {
  review_id: string;
  note_kind: 'reply' | 'standalone';
  body_length: number;
}): void {
  trackAnonymousEvent(AnalyticsEvents.SCRIPT_REVIEW_NOTE_POSTED, {
    author_kind: 'guest',
    ...props,
  });
}
